import { useState, useEffect } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add, ExpandMore, CheckCircle, ErrorOutline, Close } from "@mui/icons-material";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import EventSearch from "../../../components/common/EventSearch";
import TableComponent from "../../../components/common/TableComponent";
import ActionButtons from "../../../components/common/ActionButtons";
import { BackForeColor } from "../../../components/common/BackForeColor";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import TotalValueItem from "../../event/components/TotalValueItem";
import {
  generateNoDataValues,
  getOperationalStatusNameFromId,
  getOperationalStatusStyleName,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import { getInputLabelProps } from "../../../common/appStyles";
import { getUserName, getSettlementNotProcessedMessage } from "../../../common/EntitiesManagement";
import { SETTLEMENT_ENDPOINT } from "../../../services/config";

const STEP_LABELS = [
  localize.general_information,
  localize.service_orders,
  localize.settlement_process_title,
  localize.settlement_results_title,
];

export default function NewSettlementWizard({ show, setShow }) {
  const { config } = useGlobalContext();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [searchSink, setSearchSink] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [unexpectedError, setUnexpectedError] = useState(null);
  const [observation, setObservation] = useState("");
  const [overwrite, setOverwrite] = useState(false);

  const resetState = () => {
    setActiveStep(0);
    setSelectedEvents([]);
    setSearchSink([]);
    setShowSearch(false);
    setIsProcessing(false);
    setResultData(null);
    setUnexpectedError(null);
    setObservation("");
    setOverwrite(false);
  };

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [show]);

  useEffect(() => {
    if (searchSink?.length > 0) {
      setSelectedEvents((prev) => {
        const merged = [...prev];
        searchSink.forEach((evt) => {
          if (!merged.some((x) => x.Id === evt.Id)) {
            merged.push(evt);
          }
        });
        return merged;
      });
    }
  }, [searchSink]);

  const removeEvent = (eventId) => {
    setSelectedEvents((prev) => prev.filter((x) => x.Id !== eventId));
  };

  const requestSettlement = async () => {
    setIsProcessing(true);
    setUnexpectedError(null);
    try {
      const body = {
        eventIds: selectedEvents.map((e) => e.Id).join(","),
        createdBy: getUserName(),
        Observation: observation,
        Mode: overwrite ? "OVERWRITE" : null,
      };
      const resp = await axios.post(`${SETTLEMENT_ENDPOINT}`, body, config);
      if (resp?.data?.responseCode === 1) {
        setUnexpectedError(
          resp?.data?.responseMessage ?? localize.settlement_unexpected_error,
        );
      } else {
        setResultData(resp?.data?.responseData ?? resp?.data ?? null);
      }
    } catch (error) {
      setUnexpectedError(
        error?.response?.data?.Data ??
          error?.response?.data ??
          error?.message ??
          localize.error_unexpected,
      );
    } finally {
      setIsProcessing(false);
      setActiveStep(3);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;
    setShow(false);
  };

  const handleAcceptSuccess = () => {
    setShow(false);
    window.location.reload(false);
  };

  const renderSelectedEventsTable = () => (
    <TableComponent
      noPadding
      mobileCardMinHeight={50}
      firstColumnTitle={localize.actions}
      columnTitles={[
        localize.code,
        localize.event_type,
        localize.operational_status,
        localize.execution_status,
        localize.proceeding,
        localize.client_name,
        localize.total_value,
      ]}
      results={
        selectedEvents?.length > 0
          ? selectedEvents.map((item) => ({
              key: item.Id,
              data: [
                <ActionButtons
                  hideEditButton
                  key={`${item.Id}_action`}
                  onclickForDeleteButton={() => removeEvent(item.Id)}
                />,
                <Link
                  color="primary"
                  underline="always"
                  href="#"
                  onClick={() => {
                    window.open(`/events/detail/${item.Id}`, "_blank");
                  }}
                >{`${item.Code}`}</Link>,
                <BackForeColor
                  backColor={item?.EventType?.BackgroundColor}
                  foreColor={item?.EventType?.ForegroundColor}
                  text={item?.EventType?.Name}
                />,
                <p
                  style={{ borderRadius: "40px" }}
                  className={`rounded p-2 ${getOperationalStatusStyleName(
                    item.OperationalStatus,
                  )}`}
                >
                  {getOperationalStatusNameFromId(item.OperationalStatus)}
                </p>,
                <BackForeColor
                  backColor={
                    item.OperationalStatus === 0
                      ? "white"
                      : item.CurrentStatus?.BackgroundColor
                  }
                  foreColor={
                    item.OperationalStatus === 0
                      ? "red"
                      : item.CurrentStatus?.ForegroundColor
                  }
                  text={
                    item.OperationalStatus === 0
                      ? localize.cancelled
                      : item.CurrentStatus?.Name
                  }
                  styleName={
                    item.OperationalStatus === 0
                      ? "cancelled_operational_status"
                      : ""
                  }
                />,
                <ValueOrNoDataLabel data={item.ProceedingInfo?.Name} />,
                <ValueOrNoDataLabel
                  data={item.ReporterName ?? item.Client?.Name}
                />,
                <TotalValueItem item={item} />,
              ],
            }))
          : [generateNoDataValues(8)]
      }
    />
  );

  const renderWelcomeStep = () => (
    <Box className="flex flex-col gap-3 w-full">
      <Typography
        className="text-center"
        sx={{ fontSize: "0.95rem", fontWeight: 600 }}
      >
        {localize.new_settlement_intro}
      </Typography>
      <Alert severity="info">
        <span className="text-xs">{localize.new_settlement_req_personal}</span>
      </Alert>
      <Alert severity="info">
        <span className="text-xs">{localize.new_settlement_req_status}</span>
      </Alert>
      <Alert severity="info">
        <span className="text-xs">
          {localize.new_settlement_req_not_settled}
        </span>
      </Alert>
      <Alert severity="info">
        <span className="text-xs">
          {localize.new_settlement_req_additional_costs}
        </span>
      </Alert>
      <Alert severity="info">
        <span className="text-xs">
          {localize.new_settlement_req_service_type_payment_type}
        </span>
      </Alert>
      <Alert severity="info">
        <span className="text-xs">
          {localize.new_settlement_req_generated_status}
        </span>
      </Alert>
    </Box>
  );

  const renderOrdersStep = () => (
    <Box className="flex flex-col gap-3 w-full">
      <Box className="flex items-center justify-between gap-2 w-full">
        <Typography sx={{ fontSize: "0.9rem", color: "#4B5563", flex: 1 }}>
          {localize.settlement_orders_subtitle}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="success"
          className="app_general_button_data app_success_button btn-sm"
          onClick={() => {
            setSearchSink([]);
            setShowSearch(true);
          }}
        >
          <span className="mr-2">{localize.add}</span>
          <Add style={{ width: "16px", height: "16px" }} />
        </Button>
      </Box>
      {showSearch && (
        <EventSearch
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          setResultList={setSearchSink}
        />
      )}
      {renderSelectedEventsTable()}
    </Box>
  );

  const renderProcessStep = () => (
    <Box className="flex flex-col gap-3 w-full items-center">
      <Typography
        className="text-center"
        sx={{ fontSize: "0.9rem", color: "#4B5563" }}
      >
        {localize.settlement_process_subtitle}
      </Typography>
      <Typography
        className="text-center"
        sx={{ fontSize: "0.9rem", color: "#4B5563" }}
      >
        {localize.settlement_process_subtitle_additional}
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
        {`${localize.settlement_orders_added}: ${selectedEvents.length}`}
      </Typography>
      <TextField
        label={localize.observations}
        value={observation}
        onChange={(e) =>
          setObservation(
            e.target.value.slice(
              0,
              MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
            ),
          )
        }
        multiline
        minRows={3}
        maxRows={6}
        fullWidth
        disabled={isProcessing}
        InputLabelProps={getInputLabelProps()}
        inputProps={{
          maxLength: MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
        }}
        helperText={`${observation.length}/${MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION}`}
      />
      <Box className="w-full">
        <FormControlLabel
          control={
            <Switch
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              disabled={isProcessing}
            />
          }
          label={
            <span className="text-xs">
              {localize.settlement_overwrite_toggle}
            </span>
          }
        />
      </Box>
      {isProcessing && (
        <Alert severity="warning" className="w-full">
          <span className="text-xs">
            {localize.settlement_in_progress_warning}
          </span>
        </Alert>
      )}
      <LoadingButton
        loading={isProcessing}
        variant="contained"
        sx={{
          backgroundColor: "var(--app-bg, #10366E)",
          color: "var(--app-fg, #ffffff)",
          "&:hover": { backgroundColor: "var(--app-bg, #10366E)" },
          mt: 1,
        }}
        onClick={requestSettlement}
      >
        {localize.settlement_request_button}
      </LoadingButton>
    </Box>
  );

  const renderSettledOrdersAccordion = () => {
    const notProcessedIds = new Set(
      (resultData?.NotProcessed ?? []).map((np) => np.Id),
    );
    const settledEvents = selectedEvents.filter(
      (e) => !notProcessedIds.has(e.Id),
    );
    if (settledEvents.length === 0) return null;
    return (
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {localize.settlement_settled_orders_accordion}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            className="flex flex-col gap-2"
            sx={{ maxHeight: "30vh", overflowY: "auto" }}
          >
            {settledEvents.map((evt) => (
              <Card key={evt.Id} variant="outlined" sx={{ borderRadius: "8px" }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Link
                    color="primary"
                    underline="always"
                    href="#"
                    sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                    onClick={() => {
                      window.open(`/events/detail/${evt.Id}`, "_blank");
                    }}
                  >
                    {`${evt.Code}`}
                  </Link>
                </CardContent>
              </Card>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderNotProcessedAccordion = () => {
    if (!(resultData?.NotProcessed?.length > 0)) return null;
    return (
      <Accordion variant="outlined" disableGutters defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {localize.settlement_errors_accordion}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            className="flex flex-col gap-2"
            sx={{ maxHeight: "30vh", overflowY: "auto" }}
          >
            {resultData.NotProcessed.map((np, index) => {
              const npId = np?.Id ?? np?.id ?? "";
              const npCode = np?.Code ?? np?.code ?? npId;
              const npError = np?.Error ?? np?.error ?? "";
              const message =
                getSettlementNotProcessedMessage(npError) || npError;
              return (
                <div
                  key={`${npId}-${index}`}
                  style={{
                    border: "1px solid #FCA5A5",
                    backgroundColor: "#FEF2F2",
                    borderRadius: "8px",
                    padding: "8px 12px",
                  }}
                >
                  <div
                    onClick={() => {
                      if (npId) {
                        window.open(`/events/detail/${npId}`, "_blank");
                      }
                    }}
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#B91C1C",
                      cursor: npId ? "pointer" : "default",
                      textDecoration: npId ? "underline" : "none",
                    }}
                  >
                    {String(npCode ?? "")}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#4B5563", marginTop: 2 }}>
                    {message}
                  </div>
                </div>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderResultsStep = () => {
    if (unexpectedError) {
      return (
        <Box className="flex flex-col gap-3 w-full">
          <Box className="flex flex-col items-center gap-2">
            <ErrorOutline sx={{ color: "#C62828", fontSize: 44 }} />
            <Typography className="text-center" sx={{ fontSize: "0.9rem" }}>
              {localize.settlement_unexpected_error}
            </Typography>
          </Box>
          <Alert severity="error">
            <span className="text-xs">{`${unexpectedError}`}</span>
          </Alert>
        </Box>
      );
    }

    if (resultData?.Id === "NO") {
      return (
        <Box className="flex flex-col gap-3 w-full">
          <Box className="flex flex-col items-center gap-2">
            <ErrorOutline sx={{ color: "#C62828", fontSize: 44 }} />
            <Typography className="text-center" sx={{ fontSize: "0.9rem" }}>
              {localize.settlement_no_orders_to_settle}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (resultData?.Id) {
      const notProcessedCount = resultData?.NotProcessed?.length ?? 0;
      const hasNotProcessed = notProcessedCount > 0;
      const settledCount = Math.max(
        0,
        selectedEvents.length - notProcessedCount,
      );
      return (
        <Box className="flex flex-col gap-3 w-full">
          <Box className="flex flex-col items-center gap-2">
            <CheckCircle sx={{ color: "#2E7D32", fontSize: 48 }} />
            <Typography className="text-center" sx={{ fontSize: "0.95rem" }}>
              {localize.settlement_created_success}
            </Typography>
          </Box>

          <Box className="flex gap-2 w-full">
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                borderRadius: "8px",
                borderColor: "#2E7D32",
                backgroundColor: "#E8F5E9",
              }}
            >
              <CardContent
                className="flex flex-col items-center"
                sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
              >
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#2E7D32" }}>
                  {settledCount}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#2E7D32" }}>
                  {localize.settlement_settled_count_label}
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                flex: 1,
                borderRadius: "8px",
                borderColor: hasNotProcessed ? "#C62828" : "#E0E0E0",
                backgroundColor: hasNotProcessed ? "#FFEBEE" : "transparent",
              }}
            >
              <CardContent
                className="flex flex-col items-center"
                sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
              >
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: hasNotProcessed ? "#C62828" : "#4B5563",
                  }}
                >
                  {notProcessedCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: hasNotProcessed ? "#C62828" : "#4B5563",
                  }}
                >
                  {localize.settlement_not_processed_count_label}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {hasNotProcessed && (
            <Alert severity="warning">
              <span className="text-xs">
                {localize.settlement_created_with_not_processed}
              </span>
            </Alert>
          )}
          {renderSettledOrdersAccordion()}
          {renderNotProcessedAccordion()}
        </Box>
      );
    }

    return (
      <Box className="flex flex-col gap-3 w-full">
        <Box className="flex flex-col items-center gap-2">
          <ErrorOutline sx={{ color: "#C62828", fontSize: 44 }} />
          <Typography className="text-center" sx={{ fontSize: "0.9rem" }}>
            {localize.settlement_not_created}
          </Typography>
        </Box>

        {renderNotProcessedAccordion()}
      </Box>
    );
  };

  const renderStepBody = () => {
    switch (activeStep) {
      case 0:
        return renderWelcomeStep();
      case 1:
        return renderOrdersStep();
      case 2:
        return renderProcessStep();
      case 3:
        return renderResultsStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (activeStep === 3) {
      const isSuccess =
        !unexpectedError && !!resultData?.Id && resultData.Id !== "NO";
      return (
        <Button
          variant="contained"
          sx={{
            backgroundColor: "var(--app-bg, #10366E)",
            color: "var(--app-fg, #ffffff)",
            "&:hover": { backgroundColor: "var(--app-bg, #10366E)" },
          }}
          onClick={isSuccess ? handleAcceptSuccess : handleClose}
        >
          {isSuccess ? localize.accept : localize.close}
        </Button>
      );
    }

    return (
      <>
        {activeStep > 0 && (
          <Button
            variant="text"
            color="inherit"
            disabled={isProcessing}
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          >
            {localize.previous}
          </Button>
        )}
        {activeStep < 2 && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: "var(--app-bg, #10366E)",
              color: "var(--app-fg, #ffffff)",
              "&:hover": { backgroundColor: "var(--app-bg, #10366E)" },
            }}
            disabled={activeStep === 1 && selectedEvents.length === 0}
            onClick={() => setActiveStep((s) => s + 1)}
          >
            {localize.next}
          </Button>
        )}
      </>
    );
  };

  return (
    <Dialog
      open={show}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown
      PaperProps={{ sx: { maxHeight: "70vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <span>{STEP_LABELS[activeStep]}</span>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isProcessing}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            "& .MuiStepLabel-labelContainer": {
              display: { xs: "none", sm: "block" },
            },
            "& .MuiStepLabel-label": {
              fontSize: { sm: "0.75rem", md: "0.875rem" },
            },
            "& .MuiStepIcon-root.Mui-active": {
              color: "var(--app-bg, #10366E)",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: "var(--app-bg, #10366E)",
            },
            "& .MuiStepIcon-text": {
              fill: "var(--app-fg, #ffffff)",
            },
          }}
        >
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepBody()}
      </DialogContent>
      <Divider />
      <DialogActions>{renderFooter()}</DialogActions>
    </Dialog>
  );
}
